import API from '@aws-amplify/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import i18n from '../../../../i18n';
import React, { useContext, useEffect, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import classNames from 'classnames';

import { ProtectedRouteContext } from '../../../Context';

import './Signature.scss';

export default function Signature(props) {
    const question = props.question;
    const appInstanceId = props.appInstanceId;
    const answerUpdated = props.answerUpdated;
    const disabled = props.disabled;
    const shouldShowErrors = props.shouldShowErrors;
    const previouslyChosen = question.chosenOptions && question.chosenOptions.length > 0 ? question.chosenOptions[0] : undefined;

    const [t] = useTranslation();
    const [strokesMade, setStrokesMade] = useState(false);
    const { userSettings } = useContext(ProtectedRouteContext);
    const [lang, setLang] = useState(
        typeof userSettings?.LanguagePreference === 'string' ? userSettings.LanguagePreference : i18n.language
    );
    const [chosenVal, setChosenVal] = useState(previouslyChosen);
    const [saving, setSaving] = useState(false);
    const [pngUrl, setPngUrl] = useState('');

    /**Reference to the signature pad
     * @type SignatureCanvas
     */
    var sigPad = {};

    useEffect(() => {
        (async () => {
            if (!chosenVal) return;
            setSaving(true);
            let presignedPng = await API.get('ShipyardAPI', `/application/${appInstanceId}/answer/${question.id}/signature`);
            setPngUrl(presignedPng.location);
            setSaving(false);
        })();
    }, [appInstanceId, chosenVal, question]);

    useEffect(() => {
        if (typeof userSettings?.LanguagePreference === 'string') {
            setLang(userSettings.LanguagePreference);
        }
    }, [userSettings.LanguagePreference]);

    const clearClick = () => {
        setPngUrl('');
        if (sigPad && sigPad.clear) sigPad.clear();
        answerUpdated();
        setStrokesMade(false);
    };

    const signClick = async () => {
        setSaving(true);
        const trimmedDataUrl = sigPad.getTrimmedCanvas().toDataURL('image/png');
        try {
            var updateSignatureResponse = await API.put('ShipyardAPI', `/application/${appInstanceId}/answer/${question.id}/signature`, {
                body: { signatureImage: trimmedDataUrl },
            });
        } catch (apiErr) {
            toast.error(t('General.AjaxError'));
            return;
        }

        if (!updateSignatureResponse.fileId) {
            toast.error(t('General.AjaxError'));
            return;
        }

        question.chosenOptions = [updateSignatureResponse.fileId];
        question.isValid = true;
        answerUpdated();
        setChosenVal(updateSignatureResponse.fileId);
        setSaving(false);
    };

    const sigPadUpdated = () => {
        setStrokesMade(true);
    };

    return (
        <div
            className={
                'question-wrapper signature' + (!question.isValid && shouldShowErrors ? ' invalid' : '') + (disabled ? ' disabled' : '')
            }
        >
            <div
                className={classNames({
                    label: true,
                    required: question?.minResponseRequired > 0,
                })}
            >
                <ReactMarkdown
                    children={question.text[lang]}
                    linkTarget="_blank"
                />
            </div>
            <div className="sig-wrapper">
                {saving ? (
                    <div className="saving">
                        <FontAwesomeIcon icon={faSpinner} />
                    </div>
                ) : null}
                {pngUrl ? (
                    <img
                        alt="signature"
                        src={pngUrl}
                    />
                ) : (
                    <SignatureCanvas
                        ref={(ref) => {
                            sigPad = ref;
                        }}
                        canvasProps={{ className: 'sigCanvas' }}
                        onEnd={sigPadUpdated}
                    />
                )}
            </div>

            <div className="link-buttons">
                <span onClick={clearClick}>Clear and try again</span>
                {strokesMade && !saving && !pngUrl ? <span onClick={signClick}>Apply signature</span> : null}
            </div>

            <span
                className="invalid-reason"
                id={`quest-${question.id}-err`}
            >
                {t(question.invalidReason)}
            </span>
        </div>
    );
}
